import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check, Eye, EyeOff, LogOut, PawPrint, Trash2, X } from "lucide-react";
import AdminReviews from "../components/AdminReviews";
import AdminGallery from "../components/AdminGallery";
import AdminPaymentSettings from "../components/AdminPaymentSettings";
import AdminPasswordChange from "../components/AdminPasswordChange";
import AdminServiceArea from "../components/AdminServiceArea";
import AdminProfile from "../components/AdminProfile";
import AdminInbox from "../components/AdminInbox";
import AdminRates from "../components/AdminRates";
import api from "../lib/api";
import { Logo } from "../components/Logo";

const STATUS_STYLES = {
  pending: "bg-sand text-ink border-line",
  approved: "bg-forest/10 text-forest border-forest/25",
  declined: "bg-red-50 text-red-700 border-red-200",
};

const FILTERS = ["all", "pending", "approved", "declined"];

const EMAIL_PAUSE_COPY = {
  insufficient_credits:
    "The account is out of email credits, so approval and decline emails can't reach customers. In Emergent chat, open Profile → Manage plan → Universal Key → Add balance (or enable auto top-up).",
  daily_limit_reached:
    "Today's Universal Key daily spend limit is used up — sending resumes on its own after the limit resets, or raise the limit under Universal Key → Buy Credits in Emergent.",
  integration_disabled:
    "The email integration is switched off — turn it back on under Integrations in Emergent.",
};

const StatusChip = ({ status }) => (
  <span
    data-testid={`status-chip-${status}`}
    className={`inline-flex rounded-full border px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${STATUS_STYLES[status]}`}
  >
    {status}
  </span>
);

const Admin = () => {
  const [user, setUser] = useState("loading");
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [tab, setTab] = useState("bookings");
  const [filter, setFilter] = useState("all");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineNote, setDeclineNote] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const load = () => {
    api
      .get("/bookings")
      .then((r) => setBookings(r.data))
      .catch(() => toast.error("Could not load bookings — please refresh."));
    api
      .get("/reviews/all")
      .then((r) => setReviews(r.data))
      .catch(() => toast.error("Could not load reviews — please refresh."));
    api
      .get("/gallery")
      .then((r) => setGallery(r.data))
      .catch(() => toast.error("Could not load gallery — please refresh."));
    api
      .get("/messages/all")
      .then((r) => setInbox(r.data))
      .catch(() => toast.error("Could not load inbox — please refresh."));
    api
      .get("/admin/email-health")
      .then((r) => setEmailHealth(r.data))
      .catch(() => setEmailHealth(null));
  };

  const reviewStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/reviews/${id}/status`, { status });
      setReviews((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      toast.success(`Review ${status}.`);
    } catch {
      toast.error("Could not update this review.");
    }
  };

  const inboxReply = async (threadId, body) => {
    try {
      const { data } = await api.post(`/messages/${threadId}/reply`, { body });
      setInbox((prev) => prev.map((t) => (t.id === data.id ? data : t)));
      toast.success("Reply sent.");
      return data;
    } catch {
      toast.error("Could not send your reply.");
      return null;
    }
  };

  const inboxToggleRead = async (threadId) => {
    try {
      const { data } = await api.patch(`/messages/${threadId}/read`);
      setInbox((prev) => prev.map((t) => (t.id === data.id ? data : t)));
    } catch {
      toast.error("Could not update this conversation.");
    }
  };

  const reviewDelete = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted.");
    } catch {
      toast.error("Could not delete this review.");
    }
  };

  const setPayment = async (id, payment_status) => {    try {
      const { data } = await api.patch(`/bookings/${id}/payment`, {
        payment_status,
        payment_method: payment_status === "paid" ? "manual" : "",
      });
      setBookings((prev) => prev.map((b) => (b.id === data.id ? data : b)));
      toast.success(`Payment marked ${data.payment_status.replace("_", " ")}.`);
    } catch {
      toast.error("Could not update the payment status.");
    }
  };

  useEffect(() => {
    if (user && user !== "loading") load();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const base = typeof detail === "string" ? detail : "Login failed. Please try again.";
      setLoginError(`${base} Tip: browsers sometimes auto-fill an old saved login — retype both fields from scratch.`);
      setEmail("");
      setPassword("");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("ahh_access_token");
    setUser(null);
    setBookings([]);
  };

  const setStatus = async (id, status, note = "") => {
    try {
      const { data } = await api.patch(`/bookings/${id}/status`, { status, note });
      setBookings((prev) => prev.map((b) => (b.id === data.id ? data : b)));
      toast.success(`Request ${data.reference} ${status}.`);
    } catch {
      toast.error("Could not update this booking.");
    }
  };

  const deleteBooking = async (ref) => {
    if (!window.confirm(`Delete booking ${ref}? This can't be undone.`)) return;
    try {
      await api.delete(`/bookings/${ref}`);
      setBookings((prev) => prev.filter((b) => b.reference !== ref));
      toast.success(`Booking ${ref} deleted.`);
    } catch {
      toast.error("Could not delete this booking.");
    }
  };

  const counts = useMemo(() => {
    const c = { all: bookings.length, pending: 0, approved: 0, declined: 0 };
    bookings.forEach((b) => {
      c[b.status] = (c[b.status] || 0) + 1;
    });
    return c;
  }, [bookings]);

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  const whenText = (b) => {
    if (!b.start_date) return "—";
    const start = format(new Date(`${b.start_date}T00:00:00`), "EEE, MMM d");
    if (b.drop_in_days?.length) {
      return `${b.drop_in_days.length} day${b.drop_in_days.length !== 1 ? "s" : ""}: ` + b.drop_in_days.map((d) => {
        const w = d.slot_1 ? `${d.slot_1} & ${d.slot_2 || d.slot_1}` : d.time_slot;
        const w3 = d.third_visit && d.slot_3 ? ` & ${d.slot_3}` : "";
        return `${format(new Date(`${d.date}T00:00:00`), "MMM d")} ${w}${w3}${d.third_visit ? " (+3rd)" : ""}`;
      }).join(" · ");
    }
    if (b.end_date) {
      const end = format(new Date(`${b.end_date}T00:00:00`), "MMM d");
      const times = b.start_time || b.end_time ? ` · in ${b.start_time || "—"} / out ${b.end_time || "—"}` : "";
      return `${start} – ${end}${times}`;
    }
    const walk = b.service_slug === "dog-walking" && b.walk_length ? ` · ${b.walk_length}` : "";
    return `${start} · ${b.time_slot}${walk}`;
  };

  const petsText = (b) => {
    if (!b.dogs_count && !b.cats_count) return b.pet_type || "1 pet";
    const parts = [];
    if (b.dogs_count) parts.push(`${b.dogs_count} dog${b.dogs_count > 1 ? "s" : ""}`);
    if (b.cats_count) parts.push(`${b.cats_count} cat${b.cats_count > 1 ? "s" : ""}`);
    return parts.join(" · ");
  };

  if (user === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-inkSoft">Warming up…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md rounded-3xl border border-line bg-white p-10 shadow-[0_20px_50px_rgba(42,42,42,0.07)]"
          data-testid="admin-login-card"
        >
          <Logo />
          <h1 className="mt-8 font-display text-3xl font-medium text-ink">Ari&apos;s dashboard</h1>
          <p className="mt-2 text-sm text-inkSoft">Sign in to review booking requests.</p>
          <form onSubmit={login} className="mt-8 space-y-5">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Email</span>
              <input
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="mt-2 w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Password</span>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-login-password"
                  className="mt-2 w-full rounded-2xl border border-line bg-cream px-5 py-4 pr-14 text-base text-ink transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="admin-login-toggle-password"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-inkSoft transition-[color] duration-200 hover:text-ink"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <span className="mt-2 block text-xs text-inkSoft">
                Tap the eye to see what&apos;s typed — browsers sometimes fill an old password without telling you.
              </span>
            </label>
            {loginError && (
              <p data-testid="admin-login-error" className="text-xs text-red-600">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-submit"
              className="w-full rounded-full bg-marmalade py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-[background-color] duration-300 hover:bg-marmalade-deep disabled:opacity-60"
            >
              {loggingIn ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-line bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1300px] items-center justify-between px-6 py-4 md:px-10">
          <Logo />
          <div className="flex items-center gap-5">
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-inkSoft sm:block">
              {user.email}
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-[background-color] duration-300 hover:bg-sand"
            >
              <LogOut className="h-3.5 w-3.5" />
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-6 py-12 md:px-10">
        {emailHealth?.failing && (
          <div
            data-testid="admin-email-health-banner"
            role="alert"
            className="mb-8 rounded-3xl border border-marmalade/40 bg-marmalade/10 p-6 md:p-7"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-marmalade">Email alert</p>
            <p className="mt-2 font-display text-xl font-medium text-ink md:text-2xl">
              Customer emails aren&apos;t sending right now
            </p>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-inkSoft">
              {EMAIL_PAUSE_COPY[emailHealth.code] ||
                "Email sending is temporarily unavailable. Check your Emergent integrations and Universal Key balance — this alert clears itself as soon as sending works again."}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade">Ari&apos;s dashboard</p>
            <h1 className="mt-3 font-display text-4xl font-medium tracking-tight text-ink md:text-5xl">
              {tab === "bookings"
                ? "Booking requests"
                : tab === "reviews"
                ? "Customer reviews"
                : tab === "gallery"
                ? "Pet gallery"
                : tab === "inbox"
                ? "Private inbox"
                : "Settings"}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            <button
              onClick={() => setTab("bookings")}
              data-testid="admin-tab-bookings"
              className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                tab === "bookings" ? "border-ink bg-ink text-cream" : "border-line bg-white text-inkSoft hover:text-ink"
              }`}
            >
              Bookings ({counts.all})
            </button>
            <button
              onClick={() => setTab("reviews")}
              data-testid="admin-tab-reviews"
              className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                tab === "reviews" ? "border-ink bg-ink text-cream" : "border-line bg-white text-inkSoft hover:text-ink"
              }`}
            >
              Reviews ({reviews.length})
            </button>
            <button
              onClick={() => setTab("gallery")}
              data-testid="admin-tab-gallery"
              className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                tab === "gallery" ? "border-ink bg-ink text-cream" : "border-line bg-white text-inkSoft hover:text-ink"
              }`}
            >
              Gallery ({gallery.length})
            </button>
            <button
              onClick={() => setTab("area")}
              data-testid="admin-tab-area"
              className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                tab === "area" ? "border-ink bg-ink text-cream" : "border-line bg-white text-inkSoft hover:text-ink"
              }`}
            >
              Settings
            </button>            <button
              onClick={() => setTab("inbox")}
              data-testid="admin-tab-inbox"
              className={`relative rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                tab === "inbox" ? "border-ink bg-ink text-cream" : "border-line bg-white text-inkSoft hover:text-ink"
              }`}
            >
              Inbox ({inbox.filter((t) => t.unread).length})
            </button>
          </div>
        </div>

        {tab === "bookings" && (
          <div className="mt-6 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`admin-filter-${f}`}
                className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                  filter === f ? "border-ink bg-ink text-cream" : "border-line bg-white text-inkSoft hover:text-ink"
                }`}
              >
                {f} ({counts[f] || 0})
              </button>
            ))}
          </div>
        )}

        {tab === "bookings" ? (
          <div className="mt-8 overflow-x-auto rounded-3xl border border-line bg-white shadow-[0_8px_30px_rgba(42,42,42,0.04)]">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-4 px-6 py-20 text-center" data-testid="admin-empty-state">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sand">
                <PawPrint className="h-6 w-6 text-marmalade" />
              </span>
              <p className="font-display text-2xl text-ink">No {filter === "all" ? "" : filter} requests yet</p>
              <p className="max-w-xs text-sm text-inkSoft">
                New booking requests from the website will land here for approval.
              </p>
            </div>
          ) : (
            <table className="w-full min-w-[900px] border-collapse text-sm" data-testid="admin-bookings-table">
              <thead>
                <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft">
                  <th className="px-6 py-4">Ref</th>
                  <th className="px-4 py-4">Service</th>
                  <th className="px-4 py-4">Total</th>
                  <th className="px-4 py-4">When</th>
                  <th className="px-4 py-4">Pet</th>
                  <th className="px-4 py-4">Owner</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4">Payment</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`admin-booking-row-${b.reference}`}
                    className="border-b border-line transition-colors duration-200 last:border-b-0 hover:bg-sand/50"
                  >
                    <td className="whitespace-nowrap px-6 py-5 font-mono text-xs text-ink">{b.reference}</td>
                    <td className="px-4 py-5 text-ink">{b.service_name}</td>
                    <td className="px-4 py-5 text-ink">
                      {b.price_total ? `$${Number(b.price_total).toFixed(2)}` : "—"}
                      {b.price_note && (
                        <span className="mt-1 block text-xs text-inkSoft" title={b.price_note}>
                          {b.price_note}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-5 text-ink">
                      {whenText(b)}
                      {(b.greet_slots || []).filter((g) => g.date).length > 0 && (
                        <span
                          className="mt-1 block text-xs text-inkSoft"
                          data-testid={`greet-slots-${b.reference}`}
                          title="Requested meet & greet times"
                        >
                          {b.greet_slots
                            .filter((g) => g.date)
                            .map((g) => `G&G: ${g.date} ${g.time}`.trim())
                            .join(" · ")}
                        </span>
                      )}
                    </td>
                    <td className="max-w-[220px] px-4 py-5 text-ink">
                      <div className="flex items-start gap-3">
                        {b.pet_photo && (
                          <img
                            src={b.pet_photo}
                            alt={b.pet_name}
                            data-testid={`pet-photo-${b.reference}`}
                            className="h-11 w-11 shrink-0 rounded-full border border-line object-cover"
                          />
                        )}
                        <div className="min-w-0">
                          <span className="font-semibold">
                            {b.pet_name}{" "}
                            <span className="font-normal text-inkSoft" data-testid={`pets-count-${b.reference}`}>
                              · {petsText(b)}
                            </span>
                          </span>
                          {b.pet_age && <span className="block text-xs text-inkSoft">Age: {b.pet_age}</span>}
                          {b.care_instructions && (
                            <span className="mt-1 block truncate text-xs text-inkSoft" title={b.care_instructions}>
                              {b.care_instructions}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-5 text-ink">
                      {b.owner_name}
                      <span className="mt-1 block text-xs text-inkSoft">{b.owner_email}</span>
                      <span className="block text-xs text-inkSoft">{b.owner_phone}</span>
                    </td>
                    <td className="px-4 py-5">
                      <StatusChip status={b.status} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-5">
                      <span
                        className={`inline-flex rounded-full border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.16em] ${
                          b.payment_status === "paid"
                            ? "border-forest/25 bg-forest/10 text-forest"
                            : b.payment_status === "awaiting_confirmation"
                            ? "border-line bg-sand text-ink"
                            : "border-line bg-white text-inkSoft"
                        }`}
                        data-testid={`payment-chip-${b.reference}`}
                      >
                        {b.payment_status === "paid"
                          ? `Paid${b.payment_method && b.payment_method !== "card" && b.payment_method !== "manual" ? ` · ${b.payment_method}` : ""}`
                          : b.payment_status === "awaiting_confirmation"
                          ? `Awaiting · ${b.payment_method}`
                          : "Unpaid"}
                      </span>
                      {b.status === "approved" && b.payment_status !== "paid" && (
                        <button
                          onClick={() => setPayment(b.id, "paid")}
                          data-testid={`admin-markpaid-btn-${b.reference}`}
                          className="mt-2 block rounded-full bg-forest px-4 py-2 font-mono text-[9px] uppercase tracking-[0.16em] text-cream transition-colors duration-200 hover:bg-ink"
                        >
                          Mark paid
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-5">
                      {b.status === "pending" ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setStatus(b.id, "approved")}
                            data-testid={`admin-approve-btn-${b.reference}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-forest px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-cream transition-[background-color] duration-200 hover:bg-ink"
                          >
                            <Check className="h-3.5 w-3.5" />
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              setDeclineTarget(b);
                              setDeclineNote("");
                            }}
                            data-testid={`admin-decline-btn-${b.reference}`}
                            className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-red-700 transition-colors duration-200 hover:bg-red-50"
                          >
                            <X className="h-3.5 w-3.5" />
                            Decline
                          </button>
                          <button
                            onClick={() => deleteBooking(b.reference)}
                            aria-label={`Delete ${b.reference}`}
                            data-testid={`admin-delete-btn-${b.reference}`}
                            className="rounded-full p-2 text-inkSoft transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => deleteBooking(b.reference)}
                            aria-label={`Delete ${b.reference}`}
                            data-testid={`admin-delete-btn-${b.reference}`}
                            className="rounded-full p-2 text-inkSoft transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft">
                            {format(new Date(b.created_at), "MMM d, h:mm a")}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>
        ) : tab === "reviews" ? (
          <AdminReviews reviews={reviews} onStatus={reviewStatus} onDelete={reviewDelete} />
        ) : tab === "gallery" ? (
          <AdminGallery items={gallery} onRefresh={load} />
        ) : tab === "area" ? (
          <div className="space-y-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <AdminProfile />
              <AdminPaymentSettings />
              <AdminPasswordChange />
            </div>
            <AdminRates />
            <AdminServiceArea />
          </div>
        ) : (
          <AdminInbox threads={inbox} onReply={inboxReply} onToggleRead={inboxToggleRead} />
        )}
      </main>
      {declineTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4" data-testid="admin-decline-modal">
          <div className="w-full max-w-md rounded-3xl bg-cream p-8 shadow-2xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-marmalade">
              Decline {declineTarget.reference}
            </p>
            <h3 className="mt-2 font-display text-2xl text-ink">Send a kind follow-up</h3>
            <p className="mt-2 text-sm leading-relaxed text-inkSoft">
              The customer gets an email explaining you can&apos;t take this sit. Add an optional personal note.
            </p>
            <textarea
              value={declineNote}
              onChange={(e) => setDeclineNote(e.target.value)}
              placeholder="e.g. I'm away those dates — I'd love to help another time!"
              rows={4}
              data-testid="admin-decline-note-input"
              className="mt-4 w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm text-ink transition-[border-color] duration-200 focus:border-marmalade focus:outline-none"
            />
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  const target = declineTarget;
                  setDeclineTarget(null);
                  setStatus(target.id, "declined", declineNote);
                }}
                data-testid="admin-decline-confirm-btn"
                className="flex-1 rounded-full bg-forest py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink"
              >
                Send decline
              </button>
              <button
                onClick={() => setDeclineTarget(null)}
                data-testid="admin-decline-cancel-btn"
                className="rounded-full border border-line px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-inkSoft transition-[color] duration-300 hover:text-ink"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
